<!-- This is used for US FTC (Payment, Switch Plan, Direct, Ex-sub) -->
<template>
    <div class="wrapper">
        <div class="padded-container--ftc">
            <div
                data-ci="summary-page"
                class="aa-layout payment-layout summary-page ftc-layout"
                :class="{ 'summary-page--signup-flow': isExSubscriber }"
                ref="paymentForm">
                <span v-if="isSubscriber" class="icon-fill-rounded__check"></span>
                <h3 :class="getTitleClass">
                    {{ getTitle }}
                </h3>
                <span
                    v-if="getSubtitle"
                    class="payment-layout__step-subtitle payment-layout__centered">
                    {{ getSubtitle }}
                </span>
                <div class="payment-layout__summary summary-page__details full-width">
                    <BillingAgreementSummary
                        :isFTC="isFTC"
                        @found-price="updatePrice"
                        :coupon="displayCoupon"
                        :couponCheckPending="couponCheckPending"
                        :couponDisclaimer="couponDisclaimerCopy"
                        @applyCoupon="handleApplyCoupon"
                        @updateOrder="updateOrder"
                        @trialEndDateFormatted="updateTrialEndDate"
                        ref="orderSummary"
                        :isFromDirect="isFromDirect"
                        :recurly="recurly"
                        :recurlyReady="recurlyReady"
                        :formConfig="orderSummaryConfig"
                        :promo="appliedCouponCode"
                        :pricing="pricing"
                        :pageAttr="pageAttr"
                        :nextNonZeroBillingPrice="nextNonZeroBillingPrice"
                        :showCheckbox="showCheckbox"
                        :couponDiscountBillingDate="couponDiscountBillingDate"
                        :recurlyAccountBalance="recurlyAccountBalance"></BillingAgreementSummary>
                    <div class="pinline" v-if="hasValidStoredPaymentMethod"></div>
                    <div :id="errorMessageElementId" role="alert">
                        <p v-if="formErrorMessage" class="error">
                            {{ formErrorMessage }}
                        </p>
                    </div>
                    <BillingPaymentMethod
                        :isFTC="isFTC"
                        :activeTab="activeTab"
                        @update:activeTab="emitActiveTab($event)"
                        :pageAttr="pageAttr"
                        @is-payment-method-form-edit="setPaymentMethodFormEnabled"
                        @update-active-tab="setActiveTab"
                        :handleProcessedPayment="handleProcessedPayment"
                        :errorMessageElementId="errorMessageElementId"
                        :twoButtonExperience="isSwitchPlan"
                        :accountBalanceCoversPurchase="accountBalanceCoversPurchase"
                        :pageTypeForTracking="pageTypeForTracking"
                        ref="paymentFormGranParent"></BillingPaymentMethod>
                    <SummaryUpgradeDowngrade :pageAttr="pageAttr" v-if="isSwitchPlan" />

                    <!-- <input type="hidden" name="recurly-token" data-recurly="token" /> -->
                    <div class="pinline" v-if="hasValidStoredPaymentMethod"></div>
                    <p
                        v-if="!isFTC"
                        v-html="getAgreementCopy()"
                        class="payment-layout__summary__agreement"></p>
                    <WaiveCoolingRights
                        v-if="isFTC"
                        :checkboxConfig="getWaiveOfCoolingRightsCheckboxConfig()"
                        :errorMessage="waiveOfCoolingRightsCheckboxErrorMessage"
                        :disclaimer="pageAttr.aa_payment_agreement_copy_ftc"
                        :buttonConfig="submitConfig"
                        :buttonState="getSubmitButtonState"
                        :showCheckbox="showCheckbox"
                        :getLegalCopyForSwitchPlan="getLegalCopyForSwitchPlan"
                        :isSwitchPlan="isSwitchPlan"
                        @change="setWaiveOfCoolingRights"
                        @submit="subscribeOrUpdate($event)" />
                    <template v-else>
                        <SimpleWaiveCoolingRights
                            v-if="needsWaiveOfCoolingRights"
                            testId="waiveOfCoolingRightsCheckbox"
                            :checkboxConfig="getWaiveOfCoolingRightsCheckboxConfig()"
                            :errorMessage="waiveOfCoolingRightsCheckboxErrorMessage"
                            @change="setWaiveOfCoolingRights($event)" />
                        <FormButton
                            :button-config="submitConfig"
                            :button-state="getSubmitButtonState"
                            @click="subscribeOrUpdate($event)" />
                    </template>
                </div>
            </div>
        </div>
        <transition name="fade">
            <!-- Overlay for Switch Plan Edit Payment is handled by SharedPaymentComponent -->
            <WaitingOverlay
                :line1="getPaymentProcessingMessage"
                :line2="$getLocale('this_will_not_take_long')"
                v-if="transactionIsProcessing" />
        </transition>
    </div>
</template>

<script>
    import { mapState, mapGetters, mapMutations, mapActions } from 'vuex';

    import couponsMixin from 'aa/vue/plugins/couponsMixin';
    import checkoutMixin from 'aa/vue/plugins/checkoutMixin';
    import switchPlanMixin from 'aa/vue/components/multisubplanpicker/switchPlanMixin';
    import commonMixins from 'aa/vue/plugins/account/commonMixins';
    import pricePrettyPrint from 'aa/vue/plugins/priceMixin';
    import currentSubscriptionMixin from 'aa/vue/components/account/currentSubscriptionMixin';
    import FormState from 'aa/vue/plugins/FormState';
    import { RECURLY_COUPON_INVALID_CODE } from 'aa/vue/constants/shared';
    import billingInfoMixin from './account/billingInfoMixin';

    import { OrderSummaryConfig, TAB } from 'aa/vue/FormConfig';
    import BillingAgreementSummary from 'aa/vue/components/BillingAgreementSummary';
    import SummaryUpgradeDowngrade from 'aa/vue/components/SummaryUpgradeDowngrade';
    import OrderSummary from 'aa/vue/components/OrderSummary';
    import BillingPaymentMethod from 'aa/vue/components/BillingPaymentMethod';
    import FormButton from 'atoms/FormButton';
    import NotificationBarModel from 'aa/vue/models/NotificationBarModel';
    import {
        FTC_COMPLIANCE_CHECKBOX_ENABLED,
        PAYMENT_EXSUB_BILLING_SUMMARY,
    } from 'helpers/featureConstants';
    import {
        FTC_COMPLIANCE_ENABLED,
        SWITCH_PLAN_WAIVE_COOLING_RIGHTS_ENABLED,
        WAIVE_COOLING_RIGHTS_ENABLED,
    } from 'aa/helpers/featureConstants';
    import { PAGE_TYPES } from 'services/Tracking';
    import {
        PAGE_TYPES as AA_PAGE_TYPES,
        ACTION_NAMES as AA_ACTION_NAMES,
    } from 'aa/vue/constants/aaTrackingConstants';
    import WaiveCoolingRights from './payment/WaiveCoolingRights';
    import CheckboxElm from 'atoms/CheckboxElm';
    import { pluralizeDuration } from 'aa/helpers/stringManipulation';
    import { COUPON_TYPES } from 'aa/vue/models/CouponDiscount';
    import { DURATION } from 'aa/vue/constants/couponConstants';
    import WaitingOverlay from 'aa/vue/atoms/WaitingOverlay';
    import { ButtonState } from 'models/ButtonConfig';
    import Product from 'aa/vue/models/Product';
    import {
        setUpThreeDSecure,
        fetchStoredPaymentIfEmpty,
        isKnownLocaleMessage,
    } from './payment/signupUtil';
    import { RECAPTCHA_FORMS } from 'recaptcha/Recaptcha';
    import {
        USE_HIDDEN_BILLING_RULES_CHECKBOX,
        REMOVE_COUPON_ON_SWITCH_PLAN,
    } from 'aa/helpers/featureConstants';
    import SimpleWaiveCoolingRights from './payment/SimpleWaiveCoolingRights';

    // Reuse the existing (already-translated) coupon-stacking message for the lose-promo ribbon.
    const SWITCH_PLAN_LOSE_PROMO_KEY = 'coupon_stacking_error_message';

    export default {
        name: 'SummaryFTC',
        mixins: [
            couponsMixin,
            pricePrettyPrint,
            checkoutMixin,
            commonMixins,
            switchPlanMixin,
            currentSubscriptionMixin,
            FormState,
            billingInfoMixin,
        ],
        components: {
            BillingAgreementSummary,
            BillingPaymentMethod,
            FormButton,
            SummaryUpgradeDowngrade,
            OrderSummary,
            WaitingOverlay,
            WaiveCoolingRights,
            CheckboxElm,
            SimpleWaiveCoolingRights,
        },
        props: {
            setUpThreeDSecure: {
                type: Function,
                default: setUpThreeDSecure,
            },
            isFromDirect: {
                type: Boolean,
                default: false,
            },
            handleProcessedPayment: {
                type: Function,
                default: () => {},
            },
            handleProcessedPaymentError: {
                type: Function,
                default: () => {},
            },
            isEditPayment: {
                type: Boolean,
                default: false,
            },
        },
        data: function () {
            return {
                payPalToken: null,
                paymentRequestData: {},
                bodyScrollPos: 0,
                orderSummaryConfig: OrderSummaryConfig,
                couponDiscountBillingDate: null,
                losePromoRibbonShown: false,
                // for paypal only (for CC go to PaymentCreditCardTab)
                recaptchaAction: this.isSwitchPlan
                    ? RECAPTCHA_FORMS.FORM_SWITCH_PLAN
                    : RECAPTCHA_FORMS.FORM_PAYMENT,
                errorMessageElementId: 'summary-page-exsub-error-message',
                paymentMethodFormEnabled: false,
                activeTab: TAB.cc,
                isFTC: !!this.$store?.getters?.featureIsActive(FTC_COMPLIANCE_ENABLED),
                lowGiftCardBalanceNotificationShown: false,
                userAppliedCouponDuringSwitchPlan: false,
            };
        },
        computed: {
            ...mapState('payment', {
                recurly: (state) => state.recurly,
                currencySymbol: (state) => state.currencySymbol,
                currencyCode: (state) => state.currencyCode,
            }),
            ...mapState(['promo']),
            ...mapGetters('plan', ['getSelectedPlan', 'isSwitchPlan']),
            ...mapGetters('user', ['isExSubscriber', 'isSubscriber']),
            ...mapGetters('ftc', [
                'getEditingStoredPaymentMethod',
                'getSubmitButtonState',
                'getTransactionProcessing',
                'getCoolingRightsWaived',
                'getNeedsCheckbox',
                'getPaymentMethodUpdateProcessing',
                'getUSStatesForCheckbox',
                'getSelectedUSState',
                'getPayPalToken',
            ]),
            ...mapGetters('storedPaymentMethod', [
                'getBillingInfo',
                'hasValidStoredPaymentMethod',
                'getAccountBalanceCoversPurchase',
            ]),
            tab() {
                return TAB;
            },
            showCheckbox() {
                return this.getNeedsCheckbox;
            },
            submitCtaText() {
                return (
                    this.pageAttr?.billing_summary_cta_form_submit_ftc ?? this.$getLocale('buy_now')
                );
            },
            submitConfig() {
                const isButtonEnabled = this.getSubmitButtonState === ButtonState.Enabled;
                const disabled =
                    !isButtonEnabled || (this.isSwitchPlan && !this.accountBalanceCoversPurchase);

                return {
                    styleClass: ['button', isButtonEnabled ? 'primary' : 'disabled'],
                    displayText: this.submitCtaText,
                    isDisabled: disabled,
                };
            },
            pageAttrCategory() {
                if (this.isExSubscriber) return 'exSub';
                if (this.isSwitchPlan) return 'switchPlan';
                return 'registered';
            },
            getTitle() {
                switch (this.pageAttrCategory) {
                    case 'exSub':
                        return (
                            this.pageAttr.paysumhdr_exsub ??
                            this.pageAttr.explainer_description ??
                            ''
                        );
                    case 'switchPlan':
                        return (
                            this.pageAttr.paysumhdr_switchplan ??
                            this.pageAttr.aa_steps_header ??
                            ''
                        );
                    default:
                        return (
                            this.pageAttr.paysumhdr_registered ??
                            this.pageAttr.explainer_title ??
                            ''
                        );
                }
            },
            getSubtitle() {
                const legacy = this.pageAttr.explainer_description_subtitle;
                switch (this.pageAttrCategory) {
                    case 'exSub':
                        return this.pageAttr.paysumsubhdr_exsub ?? legacy ?? '';
                    case 'switchPlan':
                        return this.pageAttr.paysumsubhdr_switchplan ?? legacy ?? '';
                    default:
                        return this.pageAttr.paysumsubhdr_registered ?? legacy ?? '';
                }
            },
            selectedPlan() {
                return this.$store.getters['plan/getSelectedPlan'];
            },
            getTitleClass() {
                return {
                    'payment-layout__step-title': true,
                    'payment-layout__spaced_from_icon': this.isSwitchPlan,
                };
            },
            currentPlan() {
                return this.$store.getters['plan/getCurrentPlan'];
            },
            waiveOfCoolingRightsCheckboxFtcConfig() {
                let message = '';

                const { duration, period } = this.getStartingTrialDurationAndPeriod();

                const { promoDuration, promoPeriod, discountString } = this.getPromoDetails();

                //This is the standard message where we have a free trial and no promos
                message = this.pageAttr?.['aa_checkbox_free_trial_copy'];
                const hasValidCoupon = this.coupon && !this.coupon.errorMessage;

                //Also known as an ex-subscriber. In the switch plan also the trial is not applicable
                const hasNoFreeTrial =
                    ((this.isNoTrialUser || this.isSwitchPlan) && !this.coupon.trialString) ||
                    !this.plan?.trial;

                const hasFreeTrialExtension =
                    hasValidCoupon &&
                    this.coupon?.recurlyInfo?.discount?.type === COUPON_TYPES.FREE_TRIAL;

                const hasDiscount =
                    hasValidCoupon &&
                    (this.coupon?.recurlyInfo?.discount?.type === COUPON_TYPES.FIXED ||
                        this.coupon?.recurlyInfo?.discount?.type === COUPON_TYPES.PERCENT);

                const hasForeverDiscount =
                    hasValidCoupon && this.coupon?.recurlyInfo?.duration === 'forever';

                const hasSingleUseSingleCadenceDiscount =
                    hasValidCoupon &&
                    (this.coupon?.recurlyInfo?.single_use ||
                        this.coupon?.singleUse ||
                        promoDuration === 1);

                if (hasNoFreeTrial) {
                    message = this.pageAttr?.['aa_checkbox_no_intro_offer_copy'];
                    if (hasDiscount) {
                        message = this.pageAttr?.['aa_checkbox_promo_copy_with_no_free_trial_copy'];
                    }
                    if (hasSingleUseSingleCadenceDiscount) {
                        message =
                            this.pageAttr?.[
                                'aa_checkbox_promo_copy_with_no_free_trial_copy_single'
                            ];
                    }
                    if (hasForeverDiscount) {
                        message =
                            this.pageAttr?.[
                                'aa_checkbox_promo_copy_with_no_free_trial_copy_forever'
                            ];
                    }
                } else {
                    if (hasDiscount) {
                        message = this.pageAttr?.['aa_checkbox_free_trial_with_promo_copy'];
                    }
                    if (hasSingleUseSingleCadenceDiscount) {
                        message = this.pageAttr?.['aa_checkbox_free_trial_with_promo_copy_single'];
                    }
                    if (hasForeverDiscount) {
                        message = this.pageAttr?.['aa_checkbox_free_trial_with_promo_copy_forever'];
                    }
                }

                // A free trial coupon grants the trial regardless of whether the plan has a base
                // trial, so it must win over the no-trial / single-use promo templates (a single-use
                // free trial coupon would otherwise render as "first {cadence} charged at $0.00").
                // We do not allow free trial extension for switch plan.
                if (hasFreeTrialExtension && !this.isSwitchPlan) {
                    message = this.pageAttr?.['aa_checkbox_free_trial_copy'];
                }

                if (this.isSwitchPlan && this.getSelectedPlan.switchDirection === 'downgrade') {
                    message = this.pageAttr?.['aa_checkbox_no_intro_offer_copy_downgrades'];
                }

                message = message
                    ?.replace(/{duration}/g, duration)
                    ?.replace(/{period}/g, period)
                    ?.replace(/{cadence}/g, this.selectedPlan?.priceUnit.toLowerCase())
                    ?.replace(/{price}/g, this.selectedPlan?.rawPrice)
                    ?.replace(
                        /{promoPrice}/g,
                        hasSingleUseSingleCadenceDiscount
                            ? this.pricing?.price?.now.subtotal
                            : this.pricing?.price?.next?.subtotal,
                    )
                    ?.replace(/{promoPeriod}/g, promoPeriod)
                    ?.replace(/{promoDuration}/g, `${promoDuration} ${promoPeriod}`)
                    ?.replace(/{promoSavings}/g, discountString);

                if (this.showCheckbox) {
                    // Remove the prefix that is not needed when checkbox is shown
                    if (message) {
                        message = this.removeNonCheckboxLanguageFromMessage(message);
                    }
                }

                return {
                    type: 'checkbox',
                    elementId: 'ftcWaiveCoolingRights',
                    inputLabel: message,
                    wrapperClass:
                        'payment-layout__summary__waive-cooling-rights cbs-checkbox cbs-checkbox--waive-cooling-rights',
                    classList:
                        'payment-layout__summary__waive-cooling-rights__checkbox cbs-checkbox-input',
                    checked: this.getCoolingRightsWaived,
                };
            },
            recurlyAccountBalance() {
                return this.billingInfo?.recurlyAccountBalance?.value;
            },
            accountBalanceCoversPurchase() {
                if (!this.isSwitchPlan) {
                    return false;
                }
                if (!this.recurlyAccountBalance || !this.nextNonZeroBillingPriceUnformatted) {
                    return false;
                }

                const nextNonZeroBillingPriceInCents =
                    Number(this.nextNonZeroBillingPriceUnformatted) * 100;

                const accountBalanceCoversCost =
                    this.recurlyAccountBalance >= nextNonZeroBillingPriceInCents;

                return accountBalanceCoversCost;
            },
            accountBalanceCoversPurchaseWithTax() {
                if (!this.recurlyAccountBalance || !this.nextNonZeroBillingPriceUnformatted) {
                    return false;
                }

                // 1.14 = 14% tax (tax amount high enough that ensures user can cover the cost)
                const nextNonZeroBillingPriceInCentsWithTax =
                    Number(this.nextNonZeroBillingPriceUnformatted) * 100 * 1.14;

                const accountBalanceCoversCost =
                    this.recurlyAccountBalance >= nextNonZeroBillingPriceInCentsWithTax;

                return accountBalanceCoversCost;
            },
            transactionIsProcessing() {
                return this.getTransactionProcessing;
            },
            getPaymentProcessingMessage() {
                return this.isEditPayment && this.getPaymentMethodUpdateProcessing
                    ? this.$getLocale('we_are_updating_your_payment_method')
                    : this.$getLocale('we_are_processing_your_subscription');
            },
            getLegalCopyForSwitchPlan() {
                let legalText = '';
                if (this.isSwitchPlan) {
                    if (this.getSelectedPlan.switchDirection) {
                        legalText =
                            this.getSelectedPlan.switchDirection === 'upgrade'
                                ? this.pageAttr?.['aa_switch_plan_upgrade']
                                : this.pageAttr?.['aa_switch_plan_downgrade'];
                    }
                }
                return legalText;
            },
            pageTypeForTracking() {
                let pageType = 'svod_payment';
                if (this.isSwitchPlan) {
                    pageType = this.isFromDirect
                        ? 'switchplan_directLink_payment'
                        : PAGE_TYPES.PLAN_CHANGE_CONFIRMATION;
                } else {
                    pageType = this.isFromDirect
                        ? 'purchase_directLink_payment'
                        : AA_PAGE_TYPES.PAYMENT;
                }
                return pageType;
            },
            shouldRemoveSwitchPlanCoupon() {
                if (!this.isSwitchPlan || !this.featureIsActive(REMOVE_COUPON_ON_SWITCH_PLAN)) {
                    return false;
                }
                // Forever coupons are preserved across switch plans. Detect via the synchronous
                // currentSubscription signal because this.coupon is null at the moment this runs
                // during pricing setup; it settles asynchronously via the set.coupon event.
                if (
                    this.currentSubscriptionHasForeverCoupon ||
                    this.coupon?.recurlyInfo?.duration === DURATION.FOREVER
                ) {
                    return false;
                }
                // A coupon explicitly applied during this switch-plan flow is preserved
                if (this.userAppliedCouponDuringSwitchPlan) {
                    return false;
                }
                // A pushed promo is explicit user intent for this switch-plan flow, so the
                // coupon must remain visible/applied.
                if (this.hasPushedPromo) {
                    return false;
                }
                return true;
            },
            shouldWarnLosingPromoOnSwitch() {
                if (!this.isSwitchPlan || !this.featureIsActive(REMOVE_COUPON_ON_SWITCH_PLAN)) {
                    return false;
                }
                // Don't stack on top of an explicit query-driven ribbon (e.g. coupon_stacking_error_message)
                if (this.$route.query?.notification) {
                    return false;
                }
                // Switching during a free trial forfeits the trial promotion.
                if (this.currentPlan?.on_trial) {
                    return true;
                }
                // Warn whenever the account already carries a promotion — it is lost (or
                // replaced) on switch plan, including when a ?promo= is applied to the new
                // plan — EXCEPT a forever coupon, which 2885 preserves across switch plan.
                if (!this.currentSubscriptionPromo) {
                    return false;
                }
                // A forever coupon is preserved across switch plan, so no promo is lost.
                // Detect it via the synchronous currentSubscription signal: this.coupon has
                // not settled yet when the ribbon decision runs (it loads async via set.coupon),
                // so the recurlyInfo check below cannot be relied on for the forever case.
                if (this.currentSubscriptionHasForeverCoupon) {
                    return false;
                }
                return this.coupon?.recurlyInfo?.duration !== DURATION.FOREVER;
            },
            displayCoupon() {
                // Empty object (not null) so downstream summary components that read `coupon.*`
                // without optional chaining do not throw; `promoString` still hides the discount
                // row when `recurlyInfo` is missing.
                return this.shouldRemoveSwitchPlanCoupon ? {} : this.coupon;
            },
            // The promo resolved for this request: pushed via ?promo= (persisted into the
            // sv_campaign cookie and resolved server-side) or carried from earlier in the flow.
            // The switch-plan navigation no longer forwards the query, so the URL cannot be used.
            hasPushedPromo() {
                return !!this.$store.state.promo;
            },
        },
        methods: {
            ...mapMutations('ftc', [
                'setEditingStoredPaymentMethod',
                'setPaymentMethod',
                'setFormValidated',
                'setTransactionProcessing',
                'setCoolingRightsWaived',
                'setFormErrorCode',
                'setPaymentMethodUpdateProcessing',
                'setTransactionComplete',
                'setPaymentMethodUpdateComplete',
                'setNeedsCheckbox',
                'setUSStatesForCheckbox',
            ]),
            ...mapMutations('storedPaymentMethod', [
                'setBillingInfo',
                'setAccountBalanceCoversPurchase',
            ]),
            ...mapActions('ftc', ['transferUSStatesForUse']),

            // Fires the lose-promo blue ribbon at most once, when the user is confirmed to be
            // losing a promotion on switch plan. Safe to call repeatedly (e.g. from mounted for
            // the synchronous free-trial case, and from the coupon watch once the async coupon
            // settles for the coupon-strip case).
            showLosePromoRibbonIfNeeded() {
                if (this.losePromoRibbonShown || !this.shouldWarnLosingPromoOnSwitch) {
                    return;
                }
                this.losePromoRibbonShown = true;
                this.openNotification(
                    new NotificationBarModel({
                        success: true,
                        autoClose: false,
                        message: this.$getLocale(SWITCH_PLAN_LOSE_PROMO_KEY),
                    }),
                );
            },
            handleApplyCoupon(event) {
                this.userAppliedCouponDuringSwitchPlan = true;
                this.applyCoupon(event);
            },
            removeNonCheckboxLanguageFromMessage(message) {
                // The intent here is to remove text up until after the first double quoted phrase, and an optional comma and whitespace.
                const CHECKBOXLESS_PREFIX_REGEX = /^.*?"[^"]*",?\s*/;
                let result = message.replace(CHECKBOXLESS_PREFIX_REGEX, '');
                if (result.length >= 2) {
                    result = result.charAt(0).toUpperCase() + result.slice(1);
                }
                return result;
            },
            setPaymentMethodFormEnabled(event) {
                this.paymentMethodFormEnabled = event;
                this.setEditingStoredPaymentMethod(event);
            },
            setActiveTab(activeTab) {
                this.setPaymentMethod(activeTab);
                this.handleCheckboxLogic();
            },
            getStartingTrialDurationAndPeriod() {
                let duration = 7;
                let period = 'days';

                if (this.coupon?.recurlyInfo?.discount?.trial) {
                    const { amount, unit } = this.coupon.recurlyInfo.discount.trial;
                    duration = amount;
                    period = unit;
                    period = pluralizeDuration(duration, period);
                }

                return { duration, period };
            },
            getPromoDetails() {
                let promoDuration = 7;
                let promoPeriod = 'days';
                let discountString = '';

                const { planType } = this.plan;

                if (this.coupon?.recurlyInfo) {
                    ({
                        amount: promoDuration,
                        duration: promoPeriod,
                        discountString: discountString,
                    } = this.getPromoDetailsFromRecurlyCoupon(
                        this.coupon.recurlyInfo,
                        planType,
                        this.currencySymbol,
                        this.currencyCode,
                    ));
                }

                if (promoPeriod === 'single_use') {
                    promoDuration = 1;
                    promoPeriod =
                        this.selectedPlan.planType === Product.TYPE_MONTHLY
                            ? this.$getLocale('month')
                            : this.$getLocale('year');
                }

                return { promoDuration, promoPeriod, discountString };
            },
            handleWaiveOfCoolingChecked(newValue) {
                if (!this.showCheckbox) {
                    this.setCoolingRightsWaived(true);
                    return;
                }
                this.setCoolingRightsWaived(newValue);
            },
            emitActiveTab(tab) {
                this.activeTab = tab;
                this.$emit('update:activeTab', tab);
            },
            subscribeOrUpdate(e) {
                if (
                    this.getSubmitButtonState === ButtonState.Loading ||
                    this.getSubmitButtonState === ButtonState.Disabled
                ) {
                    return false;
                }

                this.setTransactionProcessing(true);

                if (
                    this.isExSubscriber &&
                    this.hasValidStoredPaymentMethod &&
                    !this.getEditingStoredPaymentMethod
                ) {
                    // resubmit their valid payment
                    // if ex-sub has entered in new payment they go through regular flow
                    return this.submitExSubscriberPayment(e);
                }

                if (this.isSwitchPlan) {
                    return this.submitOnClicked();
                } else {
                    return this.onSubmitPurchaseFTC(e);
                }
            },
            trackSwitchPlanSummaryState() {
                const trackingParams = {
                    productPricingPlan: this.plan.planType,
                    pickPlanType: this.plan.planTier,
                    pickPlanSku: this.plan.recurlyCode,
                    productCurrentSku:
                        this?.userProduct?.product_code ?? this.currentPlan?.recurlyCode,
                    productNewSku: this.plan.recurlyCode,
                    ctaText: this.submitCtaText,
                    pageType: this.pageTypeForTracking,
                    pageUrl: window.location.href,
                    paymentOnFile: this.hasValidStoredPaymentMethod ? '1' : '0',
                };

                trackingParams.purchaseProduct = this.plan.recurlyCode;
                trackingParams.purchaseProductName = this.plan.titleForTracking;
                trackingParams.purchasePrice = this.plan.rawPrice;
                trackingParams.productOfferPeriod =
                    this.coupon?.trialString ?? this.plan?.productOfferPeriod;
                trackingParams.purchaseQuantity = '1';
                trackingParams.purchasePromoCode = this.appliedCouponCode;

                const trackingServiceInstance = this.$trackingService ?? this.trackingService;

                trackingServiceInstance.trackState(trackingParams);
            },
            trackStateForSummary() {
                if (this.isSwitchPlan) {
                    this.trackSwitchPlanSummaryState();
                } else {
                    // tracking for non switch plan is handled inside payment.js (step.js -> enterRoute)
                }
            },

            onPriceChange() {
                if (this.getAccountBalanceCoversPurchase != this.accountBalanceCoversPurchase) {
                    this.setAccountBalanceCoversPurchase(this.accountBalanceCoversPurchase);
                }

                if (
                    !this.hasValidStoredPaymentMethod &&
                    !this.accountBalanceCoversPurchaseWithTax &&
                    !this.lowGiftCardBalanceNotificationShown &&
                    this.isSwitchPlan
                ) {
                    this.openNotification(
                        new NotificationBarModel({
                            success: false,
                            message: this.$getLocale('insufficient_account_balance_to_cover_cost'),
                        }),
                    );
                    this.lowGiftCardBalanceNotificationShown = true;
                }
            },
            trackCheckboxShown() {
                const trackingParams = {
                    pageType: this.pageTypeForTracking,
                    pageUrl: window.location.href,
                    ftcCheckboxShown: this.showCheckbox ? '1' : '0',
                };
                const trackingServiceInstance = this.$trackingService ?? this.trackingService;
                trackingServiceInstance.trackAction(
                    AA_ACTION_NAMES.FTC_CHECKBOX_SHOWN,
                    trackingParams,
                );
            },
            /**
             * Sets the visibility of the waive cooling checkbox (and if it is hidden ensures it is checked)
             * Whenever user selects a new state from the payment form dropdown this function is rerun
             * 1. Checks if checkbox FF is enabled (if not checkbox not shown)
             *  - if it is enabled, checkbox always shown
             * 2. Otherwise checks based off of strategy - zip, local, dma, strict
             * 3. User can also be shown checkbox if hidden billing rules are enforced
             *  - gift flow on switch plan (no billing info)
             *  - paypal on signup
             * @param {string} stateValue
             * @return {void}
             */
            handleCheckboxLogic(stateValue) {
                const checkboxAlreadyShownAndChecked =
                    this.getNeedsCheckbox && this.getCoolingRightsWaived;

                if (checkboxAlreadyShownAndChecked) {
                    return;
                }

                const checkboxFF = this.isFTC
                    ? this.$store.getters.featureIsActive(FTC_COMPLIANCE_CHECKBOX_ENABLED)
                    : this.isSwitchPlan
                      ? this.$store.getters.featureIsActive(
                            SWITCH_PLAN_WAIVE_COOLING_RIGHTS_ENABLED,
                        )
                      : this.$store.getters.featureIsActive(WAIVE_COOLING_RIGHTS_ENABLED);

                if (this.isFTC === false) {
                    const strategy = this.billingInfo?.ftcGuidelines?.strategy;

                    // we know FF is actually false if we also have no strategy
                    if (!checkboxFF && !strategy) {
                        this.setNeedsCheckbox(false);
                        this.setCoolingRightsWaived(true);
                    }
                }

                const needsCheckbox =
                    !!checkboxFF ||
                    this.needsWaiveOfCoolingRights ||
                    this.needsCheckboxBasedOnStrategy(stateValue) ||
                    this.needsCheckboxBasedOnHiddenBillingRules();

                this.setNeedsCheckbox(needsCheckbox);
                this.setCoolingRightsWaived(!needsCheckbox);

                return;
            },
            needsCheckboxBasedOnStrategy(stateValue) {
                const statesThatNeedCheckbox = this.getUSStatesForCheckbox;
                const needsCheckbox = this.getBillingInfo?.ftcGuidelines?.needsFtcCheckbox;

                // if user has not selected a state from dropdown
                // but we know they need checkbox based on user profile zip
                if (!stateValue && needsCheckbox) {
                    return true;
                }

                // otherwise check if the current selected state needs checkbox
                const currentSelectedStateNeedsCheckbox =
                    stateValue &&
                    Array.isArray(statesThatNeedCheckbox) &&
                    statesThatNeedCheckbox.includes(stateValue);

                if (currentSelectedStateNeedsCheckbox) {
                    return true;
                }

                return false;
            },
            needsCheckboxBasedOnHiddenBillingRules() {
                if (this.$store.getters.featureIsActive(USE_HIDDEN_BILLING_RULES_CHECKBOX)) {
                    const hasNoBillingInfoOnSwitchPlan =
                        this.isSwitchPlan && this.billingInfo?.isTypeGift;
                    const isPaypalOnSignUp =
                        this.activeTab === this.tab.payPal && !this.isSwitchPlan;

                    if (hasNoBillingInfoOnSwitchPlan || isPaypalOnSignUp) {
                        return true;
                    }
                }

                return false;
            },
            getWaiveOfCoolingRightsCheckboxConfig() {
                return this.isFTC
                    ? this.waiveOfCoolingRightsCheckboxFtcConfig
                    : this.waiveOfCoolingRightsCheckboxConfig;
            },
        },

        watch: {
            'pricing.price'(newVal, oldVal) {
                if (
                    oldVal?.next?.plan !== newVal?.next?.plan &&
                    this?.selectedPlan?.rawPrice === oldVal?.next?.plan &&
                    this?.selectedPlan?.planType === 'annual' &&
                    (this.coupon.hasOwnProperty('errorMessage') ||
                        this.coupon.hasOwnProperty('recurlyInfo')) &&
                    this?.coupon?.errorMessage !== RECURLY_COUPON_INVALID_CODE
                ) {
                    this.pricing.price = oldVal;
                }
            },
            coupon(newVal, oldVal) {
                if (
                    newVal.hasOwnProperty('errorMessage') &&
                    oldVal.hasOwnProperty('recurlyInfo') &&
                    newVal?.errorMessage !== RECURLY_COUPON_INVALID_CODE &&
                    oldVal?.errorMessage !== RECURLY_COUPON_INVALID_CODE
                ) {
                    this.coupon = oldVal;
                }
                this.couponDiscountBillingDate = '';
                this.updateFirstBillingDate(newVal);
                // The coupon has settled (stripped -> {} via resetCoupon, or preserved ->
                // recurlyInfo): re-evaluate whether to show the lose-promo ribbon.
                this.showLosePromoRibbonIfNeeded();
            },
            plan(newVal) {
                if (this.coupon?.recurlyInfo) {
                    this.handleCouponChange(this.coupon.recurlyInfo, true);
                    this.updateFirstBillingDate(this.coupon);
                }
            },
            nextNonZeroBillingPriceUnformatted(newVal, oldVal) {
                if (newVal) {
                    this.onPriceChange();
                }
            },
            showCheckbox(newVal, oldVal) {
                this.trackCheckboxShown();
            },
            getSelectedUSState(newVal, oldVal) {
                this.handleCheckboxLogic(newVal);
            },
        },
        beforeDestroy() {
            this.setCoolingRightsWaived(false);
        },
        async mounted() {
            this.setTransactionProcessing(false);
            const notificationKey = this.$route.query?.notification;
            const notificationMessage = notificationKey && this.$getLocale(notificationKey);
            if (isKnownLocaleMessage(notificationMessage, notificationKey)) {
                this.openNotification(
                    new NotificationBarModel({
                        success: true,
                        autoClose: false,
                        message: notificationMessage,
                    }),
                );
            } else if (this.currentPlan?.on_trial) {
                // Free-trial loss is known synchronously at mount. The coupon-strip case is
                // deferred to the `coupon` watch, which fires once the async coupon settles.
                this.showLosePromoRibbonIfNeeded();
            }

            if (
                this.isSwitchPlan ||
                this.$store.getters.featureIsActive(PAYMENT_EXSUB_BILLING_SUMMARY)
            ) {
                await fetchStoredPaymentIfEmpty(this.$store);
            }

            if (this.hasValidStoredPaymentMethod) {
                this.$store.commit(
                    'payment/setPaymentMethod',
                    this.getBillingInfo?.isTypePaypal ? TAB.payPal : TAB.cc,
                );

                // Arms Kount even when the payment form stays collapsed (stored card
                // covers the switch). Skipped otherwise: the form auto-opens and its
                // child component already sets the real formReference on mount.
                this.$store.commit('payment/setFormReference', this.$refs.paymentForm);
            }

            this.trackStateForSummary();

            if (this.getBillingInfo?.ftcGuidelines?.states) {
                this.transferUSStatesForUse(this.getBillingInfo?.ftcGuidelines?.states);
            }

            this.handleCheckboxLogic();
        },
    };
</script>
